import { useMemo } from "react";

const ACP_REVIEWER_DISPLAY_LABEL = "ACP Editor";
const PREVIEW_DILIMITER = "; ";
const PREVIEW_CONTRIBUTORS_COUNT = 4;

const normalize = (str) => (str ? str.trim().toLowerCase() : "");

const ACP_REVIEWER = normalize("ACP Reviewer");
const SECTION_EDITOR = normalize("Section Editor");
const TOPIC_EDITOR = normalize("Topic Editor");

const GROUP_KEY = {
  ACP_REVIEWER: "ACP_REVIEWER",
  SECTION_EDITOR: "SECTION_EDITOR",
  TOPIC_EDITOR: "TOPIC_EDITOR",
  OTHER: "OTHER",
  ENDORSEMENT: "ENDORSEMENT",
};

const GROUP_KEY_BY_LABEL = {
  [ACP_REVIEWER]: GROUP_KEY.ACP_REVIEWER,
  [SECTION_EDITOR]: GROUP_KEY.SECTION_EDITOR,
  [TOPIC_EDITOR]: GROUP_KEY.TOPIC_EDITOR,
};

const getGroupKeyByLabel = (label) => {
  const normalizedLabel = normalize(label);
  return (
    normalizedLabel && (GROUP_KEY_BY_LABEL[normalizedLabel] || GROUP_KEY.OTHER)
  );
};

const formatContributorPreview = (contributor) =>
  `${contributor.givenName} ${contributor.surname} ${contributor.degrees}`;

const getEndorsment = ({ contributors, contributorLogos }) => {
  if (!contributors || !contributorLogos.acp) {
    return null;
  }
  const acpLabel = contributors.find((c) => c.acpContributor)?.acpContributor;

  if (!acpLabel) {
    return null;
  }

  return {
    src: contributorLogos.acp.src,
    alt: contributorLogos.acp.alt,
    label: acpLabel,
  };
};

export const useContributors = ({ contributors, contributorLogos }) =>
  useMemo(() => {
    const groupedContributors = contributors.reduce((acc, contributor) => {
      const groupKey =
        getGroupKeyByLabel(contributor.label) || GROUP_KEY.ENDORSEMENT;
      const updatedContributor =
        groupKey === GROUP_KEY.ACP_REVIEWER
          ? { ...contributor, label: ACP_REVIEWER_DISPLAY_LABEL }
          : contributor;

      acc[groupKey] = acc[groupKey] ?? [];
      acc[groupKey].push(updatedContributor);

      return acc;
    }, {});

    const allContributors = [
      ...(groupedContributors.ACP_REVIEWER ?? []),
      ...(groupedContributors.SECTION_EDITOR ?? []),
      ...(groupedContributors.TOPIC_EDITOR ?? []),
      ...(groupedContributors.OTHER ?? []),
    ];

    const contributorsPreview = allContributors
      .slice(0, PREVIEW_CONTRIBUTORS_COUNT)
      .map(formatContributorPreview)
      .join(PREVIEW_DILIMITER);

    const endorsement = getEndorsment({
      contributors: groupedContributors.ENDORSEMENT,
      contributorLogos,
    });

    return { contributorsPreview, allContributors, endorsement };
  }, [contributors, contributorLogos]);
